import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  ArrowRight,
  BarChart3,
  Bot,
  ChevronLeft,
  ChevronRight,
  Clock,
  Cpu,
  CreditCard,
  Database,
  FileText,
  Link as LinkIcon,
  MoreHorizontal,
  Play,
  Plus,
  Puzzle,
  Settings,
  Zap,
} from 'lucide-react';
import { useUser } from '../../context/UserContext';
import apiService from '../../services/apiService';
import { ActivityTimelineChart } from './ActivityTimelineChart';
import { ActivityFeed } from './ActivityFeed';
import { HealthGauge } from './HealthGauge';
import { PerformanceCard } from './PerformanceCard';
import { SystemCard } from './SystemCard';
import styles from './Dashboard.module.scss';
import chartStyles from '../MainContent/MainContent.module.scss';

const nf0 = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 });
const nfCompact = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });

function n0(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return nf0.format(n);
}

function n1(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return nf1.format(n);
}

function nCompact(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return nfCompact.format(n);
}

function durMs(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n)) return '—';
  if (n < 1000) return `${n0(n)}ms`;
  return `${n1(n / 1000)}s`;
}

function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

/** API may return camelCase or snake_case token fields */
function rowTotalTokens(r) {
  const v = r?.total_tokens ?? r?.totalTokens ?? r?.tokens;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function rowCalls(r) {
  const v = r?.calls ?? r?.call_count;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Unwrap common agent-metrics / models response shapes */
function normalizeMetricsResponse(res, depth = 0) {
  if (depth > 5) return [];
  const root = res?.data !== undefined ? res.data : res;
  if (Array.isArray(root)) return root;
  if (root && typeof root === 'object') {
    const keys = ['items', 'agents', 'models', 'results', 'rows', 'records', 'metrics', 'payload'];
    for (const k of keys) {
      const v = root[k];
      if (Array.isArray(v)) return v;
    }
    if (root.data != null && typeof root.data === 'object' && !Array.isArray(root.data)) {
      return normalizeMetricsResponse({ data: root.data }, depth + 1);
    }
  }
  return [];
}

function extractTimelineBuckets(res) {
  const root = res?.data ?? res;
  const ts = root?.time_series ?? root?.timeSeries ?? root?.data?.time_series ?? root?.data?.timeSeries;
  const buckets = ts?.buckets ?? [];
  return Array.isArray(buckets) ? buckets : [];
}

function getAgentStatus(agent) {
  if (agent.status) return agent.status;
  if (agent.is_active !== undefined) return agent.is_active ? 'Active' : 'Inactive';
  if (agent.state) return agent.state;
  return 'Active';
}

function tagVariantForStatus(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'inactive' || s === 'draft') return 'warn';
  if (s === 'active') return 'ok';
  return 'neutral';
}

/**
 * Hero-aligned health 0–100: workspace agents (active) vs failed KB files (attention).
 * When both counts are positive, score is the share of agents in (agents + attention).
 * When there is nothing to attend to, score is 100; empty workspace uses a neutral mid score.
 */
function computeHealthScore(activeAgents, attentionItems) {
  const A = Math.max(0, Number(activeAgents) || 0);
  const T = Math.max(0, Number(attentionItems) || 0);

  if (A === 0 && T === 0) return 92;
  if (T === 0) return 100;

  const denom = A + T;
  const pct = denom > 0 ? Math.round((100 * A) / denom) : 0;
  return Math.min(100, Math.max(14, pct));
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useUser();
  const displayName = user?.name?.split(' ')?.[0] || user?.name || 'there';

  const [stats, setStats] = useState({});
  const [loadingStats, setLoadingStats] = useState(true);
  const [metricsWindow, setMetricsWindow] = useState('24h');
  const [windowMenuAnchor, setWindowMenuAnchor] = useState(null);
  const [metricsAgentId] = useState('');
  const [agentUsage, setAgentUsage] = useState([]);
  const [modelUsage, setModelUsage] = useState([]);
  const [timelineBuckets, setTimelineBuckets] = useState([]);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [metricsError, setMetricsError] = useState(null);
  const [timelineError, setTimelineError] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);

  const [activeTab, setActiveTab] = useState('All Systems');
  const tabs = ['All Systems', 'Marketing Systems', 'Operations Systems', 'Finance Systems', 'Custom Groups'];

  const systemsScrollRef = useRef(null);
  const performanceScrollRef = useRef(null);

  const effectiveWindow = useDebouncedValue(metricsWindow, 250);
  const effectiveAgentId = useDebouncedValue(metricsAgentId, 400);
  /** Trim so whitespace-only IDs are not sent (breaks backend matching) */
  const effectiveAgentIdForApi = useMemo(() => effectiveAgentId.trim(), [effectiveAgentId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingStats(true);
        const dashboardStats = await apiService.getDashboardStats();
        const statsData = dashboardStats.data || dashboardStats;
        setStats({
          total_agents: statsData.agents?.total || 0,
          total_files: statsData.knowledge_base?.total_files || 0,
          total_folders: statsData.knowledge_base?.total_folders || 0,
          pending_files: statsData.knowledge_base?.pending_files || 0,
          processing_files: statsData.knowledge_base?.processing_files || 0,
          completed_files: statsData.knowledge_base?.completed_files || 0,
          failed_files: statsData.knowledge_base?.failed_files || 0,
          total_tools: statsData.tools?.total || 0,
          total_models: statsData.models?.total || 0,
          active_models: statsData.models?.active || 0,
          total_connected_apps: statsData.connected_apps?.total || 0,
          active_connected_apps: statsData.connected_apps?.active || 0,
          pending_connected_apps: statsData.connected_apps?.pending || 0,
          failed_connected_apps: statsData.connected_apps?.failed || 0,
        });
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        setStats({
          total_agents: 0,
          total_files: 0,
          total_folders: 0,
          recent_activity: 0,
        });
      } finally {
        setLoadingStats(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const loadAgents = async () => {
      try {
        setLoadingAgents(true);
        const res = await apiService.getAgents(1, 48);
        const list = res?.agents || [];
        setAgents(list);
      } catch (e) {
        console.error(e);
        setAgents([]);
      } finally {
        setLoadingAgents(false);
      }
    };
    loadAgents();
  }, []);

  useEffect(() => {
    const fetchMetrics = async () => {
      setLoadingMetrics(true);
      setMetricsError(null);
      setTimelineError(null);
      try {
        const [agentsRes, modelsRes] = await Promise.all([
          apiService.getAgentMetricsAgents(effectiveWindow, effectiveAgentIdForApi),
          apiService.getAgentMetricsModels(effectiveWindow, effectiveAgentIdForApi),
        ]);

        setAgentUsage(normalizeMetricsResponse(agentsRes));
        setModelUsage(normalizeMetricsResponse(modelsRes));
      } catch (e) {
        setMetricsError(e?.message || 'Failed to fetch metrics');
        setAgentUsage([]);
        setModelUsage([]);
      } finally {
        setLoadingMetrics(false);
      }
    };
    fetchMetrics();
  }, [effectiveWindow, effectiveAgentIdForApi]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await apiService.getAgentMetricsAgents(effectiveWindow, effectiveAgentIdForApi, {
          mode: 'timeseries',
          granularity: 'auto',
        });
        const buckets = extractTimelineBuckets(res);
        if (!cancelled) setTimelineBuckets(buckets);
      } catch (e) {
        if (!cancelled) {
          setTimelineError(e?.message || 'Failed to fetch timeline');
          setTimelineBuckets([]);
        }
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [effectiveWindow, effectiveAgentIdForApi]);

  const filteredAgents = useMemo(() => {
    const list = agents || [];
    const text = (a) => `${a.name || ''} ${a.agent_goal || ''} ${a.agent_role || ''}`.toLowerCase();
    switch (activeTab) {
      case 'Marketing Systems':
        return list.filter((a) => /market|campaign|content|brand|social|email/.test(text(a)));
      case 'Operations Systems':
        return list.filter((a) => /ops|operat|pipeline|monitor|support|ticket/.test(text(a)));
      case 'Finance Systems':
        return list.filter((a) => /finance|invoice|payment|collection|billing/.test(text(a)));
      case 'Custom Groups':
        return list.filter(
          (a) => !/market|campaign|ops|operat|finance|invoice/.test(text(a))
        );
      default:
        return list;
    }
  }, [agents, activeTab]);

  const filteredAgentIdsForTab = useMemo(() => {
    if (activeTab === 'All Systems') return null;
    return new Set((filteredAgents || []).map((a) => String(a.id)));
  }, [activeTab, filteredAgents]);

  /** Rows included in totals / KPIs / tables when a system tab filters agents */
  const agentUsageForSummary = useMemo(() => {
    if (filteredAgentIdsForTab === null) return agentUsage;
    if (filteredAgentIdsForTab.size === 0) return [];
    return agentUsage.filter((r) => {
      const id = String(r.agent_id ?? r.id ?? '');
      return id && filteredAgentIdsForTab.has(id);
    });
  }, [agentUsage, filteredAgentIdsForTab]);

  const usageSummary = useMemo(() => {
    const rows = agentUsageForSummary;
    const calls = rows.reduce((sum, r) => sum + rowCalls(r), 0);
    const totalTokens = rows.reduce((sum, r) => sum + rowTotalTokens(r), 0);
    const errors = rows.reduce((sum, r) => sum + Number(r.errors || 0), 0);
    const toolCalls = rows.reduce((sum, r) => sum + Number(r.tool_calls || 0), 0);
    const weightedLatency = rows.reduce(
      (sum, r) => sum + rowCalls(r) * Number(r.avg_duration_ms || r.avg_latency_ms || 0),
      0
    );
    const avgLatencyMs = calls > 0 ? weightedLatency / calls : 0;
    return { calls, totalTokens, errors, avgLatencyMs, toolCalls };
  }, [agentUsageForSummary]);

  const topAgents = useMemo(() => {
    return [...(agentUsageForSummary || [])]
      .sort((a, b) => rowTotalTokens(b) - rowTotalTokens(a))
      .slice(0, 8);
  }, [agentUsageForSummary]);

  const healthScore = useMemo(
    () => computeHealthScore(stats.total_agents || 0, stats.failed_files || 0),
    [stats.total_agents, stats.failed_files]
  );

  const metricsByAgentId = useMemo(() => {
    const m = new Map();
    agentUsage.forEach((row) => {
      const id = row.agent_id ?? row.id;
      if (id != null) m.set(String(id), row);
    });
    return m;
  }, [agentUsage]);

  const systemCards = useMemo(() => {
    const source = activeTab === 'All Systems' ? agents : filteredAgents;
    return source.slice(0, 24).map((agent) => {
      const st = getAgentStatus(agent);
      const row = metricsByAgentId.get(String(agent.id));
      const calls = row ? rowCalls(row) : 0;
      const tokens = row ? rowTotalTokens(row) : 0;
      return {
        id: String(agent.id),
        name: agent.name || `Agent ${agent.id}`,
        icon: Bot,
        tags: [{ label: st, variant: tagVariantForStatus(st) }],
        description: (agent.agent_goal || agent.agent_role || 'Autonomous agent').slice(0, 120),
        recommendationText: row
          ? `${n0(calls)} calls · ${nCompact(tokens)} tokens (${metricsWindow})`
          : `No usage in ${metricsWindow}${loadingMetrics ? '…' : ''}`,
        agentId: agent.id,
      };
    });
  }, [activeTab, filteredAgents, agents, metricsByAgentId, metricsWindow, loadingMetrics]);

  // Real activity feed not wired yet — empty keeps "Coming soon" UI in ActivityFeed
  const activityLogs = [];

  const performanceInsights = useMemo(() => {
    return topAgents.slice(0, 6).map((a) => {
      const err = Number(a.errors || 0);
      const calls = Number(a.calls || 0);
      const isPositive = err === 0 || (calls > 0 && err / calls < 0.05);
      return {
        title: `Agent #${a.agent_id ?? a.id ?? '—'}`,
        description: `${n0(calls)} calls · Avg ${durMs(a.avg_duration_ms ?? a.avg_latency_ms ?? 0)} · ${n0(err)} errors`,
        type: isPositive ? 'Clicks' : 'Cost',
        isPositive,
      };
    });
  }, [topAgents]);

  const kpiData = useMemo(
    () => [
      {
        title: 'Active Systems',
        value: loadingStats ? '—' : n0(stats.total_agents || 0),
        subtext: 'running',
        icon: Cpu,
        trend: null,
      },
      {
        title: `Agent runs (${metricsWindow})`,
        value: loadingMetrics ? '—' : n0(usageSummary.calls),
        subtext: 'executions',
        icon: Play,
        trend: null,
      },
      {
        title: 'Time saved',
        value: '36',
        subtext: 'hours',
        icon: Clock,
        trend: null,
      },
      {
        title: 'Tasks automated',
        value: loadingMetrics ? '—' : n0(usageSummary.toolCalls),
        subtext: 'tool calls',
        icon: Zap,
        trend: null,
      },
      {
        title: 'KB assets',
        value: loadingStats ? '—' : n0(stats.completed_files ?? stats.total_files ?? 0),
        subtext: 'files indexed',
        icon: FileText,
        trend: null,
      },
    ],
    [loadingStats, loadingMetrics, stats, usageSummary, metricsWindow]
  );

  const attentionCount = Number(stats.failed_files || 0);

  const scrollToAnalytics = () => {
    const el = document.getElementById('dashboard-analytics');
    el?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollHorizontal = (ref, direction) => {
    const el = ref.current;
    if (!el) return;
    const delta = Math.min(320, Math.max(240, el.clientWidth * 0.85));
    el.scrollBy({ left: direction * delta, behavior: 'smooth' });
  };

  return (
    <div className={chartStyles.content}>
      <div className={styles.dashboard}>
        <div className={styles.overviewTopBar}>
          <Typography variant="h5" fontWeight={700}>
            Overview
          </Typography>
          <div className={styles.controlsRow}>
            <button
              type="button"
              className={styles.windowPill}
              aria-haspopup="listbox"
              aria-expanded={Boolean(windowMenuAnchor)}
              aria-label="Time window"
              onClick={(e) => setWindowMenuAnchor(e.currentTarget)}
            >
              {metricsWindow}{' '}
              <ChevronLeft size={14} style={{ transform: 'rotate(-90deg)' }} aria-hidden />
            </button>
            <Menu
              anchorEl={windowMenuAnchor}
              open={Boolean(windowMenuAnchor)}
              onClose={() => setWindowMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  mt: 0.5,
                  minWidth: 120,
                  borderRadius: '10px',
                  border: '1px solid var(--border-color)',
                  boxShadow: '0 4px 14px rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              {['24h', '2d', '3d', '7d', '30d'].map((w) => (
                <MenuItem
                  key={w}
                  selected={metricsWindow === w}
                  onClick={() => {
                    setMetricsWindow(w);
                    setWindowMenuAnchor(null);
                  }}
                >
                  {w}
                </MenuItem>
              ))}
            </Menu>
          </div>
        </div>

        <section className={styles.hero}>
          <div className={styles.heroText}>
            <h1 className={styles.heroTitle}>
              <span role="img" aria-label="wave">
                👋
              </span>{' '}
              Hi, {displayName}
            </h1>
            <p className={styles.heroSub}>Here is your system health overview</p>

            <div className={styles.statusList}>
              <div className={styles.statusRow}>
                <div className={`${styles.dot} ${styles.dotGreen}`}>
                  <div className={styles.dotInner} />
                </div>
                <span>
                  <strong>{loadingStats ? '—' : n0(stats.total_agents || 0)}</strong> agents in your workspace
                </span>
              </div>
              <div className={styles.statusRow}>
                <div className={`${styles.dot} ${styles.dotAmber}`}>
                  <div className={styles.dotInner} />
                </div>
                <span>
                  <strong>{attentionCount}</strong> items need attention (failed KB)
                </span>
              </div>
            </div>

            <button type="button" className={styles.heroCta} onClick={scrollToAnalytics}>
              View detailed metrics and activity timeline
              <ArrowRight size={16} />
            </button>
          </div>
          <HealthGauge score={healthScore} />
        </section>

        <div className={styles.kpiGrid}>
          {kpiData.map((kpi) => (
            <div key={kpi.title} className={styles.kpiCard}>
              <div className={styles.kpiTop}>
                <div className={styles.kpiIconBox}>
                  <kpi.icon size={20} />
                </div>
                {kpi.trend && (
                  <div
                    className={`${styles.kpiTrend} ${
                      kpi.trend.isPositive ? styles.trendUp : styles.trendDown
                    }`}
                  >
                    {kpi.trend.isPositive ? '+' : '−'}
                    {kpi.trend.value}
                  </div>
                )}
              </div>
              <div>
                <div className={styles.kpiLabel}>{kpi.title}</div>
                <div className={styles.kpiValueRow}>
                  <span className={styles.kpiValue}>{kpi.value}</span>
                  <span className={styles.kpiSub}>{kpi.subtext}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div>
          <h3 className={styles.sectionTitle}>Quick actions</h3>
          <div className={styles.quickActions}>
            <button type="button" className={styles.quickBtn} onClick={() => navigate('/agent-create')}>
              <div className={styles.quickIcon} style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#2563eb' }}>
                <Plus size={20} />
              </div>
              <div>
                <div className={styles.quickTitle}>Create new agent</div>
                <div className={styles.quickSub}>Open the agent builder</div>
              </div>
            </button>
            <button type="button" className={styles.quickBtn} onClick={() => navigate('/configure/tools')}>
              <div className={styles.quickIcon} style={{ background: 'rgba(34, 197, 94, 0.12)', color: '#15803d' }}>
                <LinkIcon size={20} />
              </div>
              <div>
                <div className={styles.quickTitle}>Add integration</div>
                <div className={styles.quickSub}>Connect tools and data</div>
              </div>  
            </button>
            <button type="button" className={styles.quickBtn} onClick={() => navigate('/models')}>
              <div className={styles.quickIcon} style={{ background: 'rgba(227, 98, 80, 0.12)', color: 'var(--primary)' }}>
                <CreditCard size={20} />
              </div>
              <div>
                <div className={styles.quickTitle}>Models</div>
                <div className={styles.quickSub}>Manage model access</div>
              </div>
            </button>
            <button type="button" className={styles.quickBtn} onClick={() => navigate('/agents')}>
              <div className={styles.quickIcon} style={{ background: 'rgba(148, 163, 184, 0.2)', color: '#475569' }}>
                <Settings size={20} />
              </div>
              <div>
                <div className={styles.quickTitle}>All agents</div>
                <div className={styles.quickSub}>Browse and edit agents</div>
              </div>
            </button>
          </div>
        </div>

        <div className={styles.usagePanel}>
          {metricsError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {metricsError}
            </Alert>
          )}
          <div className={styles.usageHeader}>
            <h3 className={styles.usageTitle}>System usage</h3>
            <div className={styles.controlsRow}>
            
              <button type="button" className={styles.iconBtn} aria-label="More">
                <MoreHorizontal size={18} />
              </button>
            </div>
          </div>
          <div className={styles.usageGrid}>
            <div className={styles.usageCard}>
              <div className={styles.usageCardTop}>
                <h3 className={styles.usageCardTitle}>API calls ({metricsWindow})</h3>
                <div className={styles.usageCardIcon}>
                  <Puzzle size={16} />
                </div>
              </div>
              <div className={styles.usageValue}>{loadingMetrics ? '—' : n0(usageSummary.calls)}</div>
              <div className={styles.usageSub}>Requests processed</div>
              <div className={styles.usageFooter}>Agent metrics</div>
            </div>
            <div className={styles.usageCard}>
              <div className={styles.usageCardTop}>
                <h3 className={styles.usageCardTitle}>Tokens used</h3>
                <div className={styles.usageCardIcon}>
                  <Database size={16} />
                </div>
              </div>
              <div className={styles.usageValue}>{loadingMetrics ? '—' : nCompact(usageSummary.totalTokens)}</div>
              <div className={styles.usageSub}>Total consumption</div>
              <div className={styles.usageFooter}>
                {modelUsage.length > 0
                  ? `${n0(modelUsage.length)} models with usage`
                  : 'Across selected window'}
              </div>
            </div>
            <div className={styles.usageCard}>
              <div className={styles.usageCardTop}>
                <h3 className={styles.usageCardTitle}>Average response</h3>
                <div className={styles.usageCardIcon}>
                  <Clock size={16} />
                </div>
              </div>
              <div className={styles.usageValue}>{loadingMetrics ? '—' : durMs(usageSummary.avgLatencyMs)}</div>
              <div className={styles.usageSub}>Average latency</div>
              <div className={styles.usageFooter}>Weighted by calls</div>
            </div>
          </div>
        </div>

        <div id="dashboard-analytics">
          <Box className={chartStyles.panelCard} sx={{ borderRadius: '16px', width: '100%' }}>
            <Box sx={{ p: 2 }}>
              <Typography className={chartStyles.panelTitle}>Activity timeline</Typography>
              <Typography className={chartStyles.panelSub} sx={{ mb: 1.5 }}>
                API calls and token usage over time
              </Typography>
              {timelineError && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  {timelineError}
                </Alert>
              )}
              {loadingMetrics ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading timeline…</Typography>
                </Box>
              ) : (
                <ActivityTimelineChart buckets={timelineBuckets} window={metricsWindow} />
              )}
            </Box>
          </Box>
        </div>

        <section className={styles.systemsSection}>
          <div className={styles.systemsHeader}>
            <h3 className={styles.systemsTitle}>Active Autonomous Systems</h3>
            <div className={styles.systemsHeaderNav}>
              <button
                type="button"
                className={styles.systemsNavBtn}
                aria-label="Scroll agents left"
                onClick={() => scrollHorizontal(systemsScrollRef, -1)}
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                className={styles.systemsNavBtnDark}
                aria-label="Scroll agents right"
                onClick={() => scrollHorizontal(systemsScrollRef, 1)}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>

          <div className={styles.tabsRowOuter}>
            <div className={styles.tabsRow}>
              {tabs.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
              <button type="button" className={styles.tabCreateGroup} onClick={() => navigate('/agents')}>
                <Plus size={14} aria-hidden />
                Create Group
              </button>
            </div>
          </div>

          <div
            ref={systemsScrollRef}
            className={`${styles.systemsScroll} scrollable-no-scrollbar`}
          >
            {loadingAgents ? (
              <Typography color="text.secondary">Loading agents…</Typography>
            ) : systemCards.length === 0 ? (
              <Typography color="text.secondary">No agents match this filter.</Typography>
            ) : (
              systemCards.map((sys) => (
                <SystemCard
                  key={sys.id}
                  system={sys}
                  onClick={() => navigate(`/agent-create?id=${sys.agentId}`)}
                />
              ))
            )}
          </div>

          <div className={styles.carouselDots} aria-hidden>
            <span className={`${styles.carouselDot} ${styles.carouselDotActive}`} />
            <span className={styles.carouselDot} />
            <span className={styles.carouselDot} />
          </div>
        </section>

        <div className={styles.activityPerformanceStack}>
          <ActivityFeed logs={activityLogs} />

          <div className={styles.insightPanel}>
            <div className={styles.insightAside}>
              <div className={styles.insightIconCircle}>
                <Cpu size={48} strokeWidth={1.75} aria-hidden />
              </div>
              <h3 className={styles.insightAsideTitle}>System Health</h3>
              <p className={styles.insightAsideText}>
                Real-time performance metrics across all active agent nodes
              </p>
            </div>
            <div className={styles.insightMain}>
              <div className={styles.insightHead}>
                <div className={styles.insightHeadLabel}>
                  <BarChart3 size={14} />
                  <span>Performance Insight</span>
                </div>
                <div className={styles.insightHeadNav}>
                  <button
                    type="button"
                    className={styles.insightNavBtn}
                    aria-label="Scroll insights left"
                    onClick={() => scrollHorizontal(performanceScrollRef, -1)}
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <button
                    type="button"
                    className={styles.insightNavBtnDark}
                    aria-label="Scroll insights right"
                    onClick={() => scrollHorizontal(performanceScrollRef, 1)}
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
              <div
                ref={performanceScrollRef}
                className={`${styles.performanceScroll} scrollable-no-scrollbar`}
              >
                {performanceInsights.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No metrics yet for this window.
                  </Typography>
                ) : (
                  performanceInsights.map((ins, idx) => <PerformanceCard key={`${ins.title}-${idx}`} {...ins} />)
                )}
              </div>
              <div className={styles.performanceDots} aria-hidden>
                <span className={`${styles.performanceDot} ${styles.performanceDotActive}`} />
                <span className={styles.performanceDot} />
                <span className={styles.performanceDot} />
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
